import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { domainService } from '../services/domainService';
import { policyService } from '../services/policyService';
import { teamsService } from '../services/teamsService';
import { domainFeedService } from '../services/domainFeedService';
import { apiClient } from '../services/apiClient';
import { useMSALAuth } from './MSALAuthContext';

// Import authoritative Domain interface from data layer
import { Domain } from '../types/dataLayer';

// Types

export interface Policy {
  id: string;
  name: string;
  type: 'managed' | 'unmanaged';
  userGroups: string[];
  status: 'active' | 'inactive' | 'drift_detected' | 'syncing';
  lastSync?: string;
  domainCount?: number;
  driftCount?: number;
  allowedDomains?: string[];
  blockedDomains?: string[];
}

export interface SystemStatus {
  lsegFeed: 'connected' | 'disconnected' | 'loading';
  teamsStatus: 'connected' | 'disconnected' | 'loading';
  lastLsegSync?: string;
  pendingApprovals: number;
}

export interface UnifiedAppState {
  // Data
  domains: Domain[];
  policies: Policy[];
  systemStatus: SystemStatus;
  
  // UI State
  isLoading: boolean;
  currentView: string;
  sidebarCollapsed: boolean;
  
  // Filters
  domainFilter: {
    status: string | null;
    source: string | null;
    search: string;
  };
  policyFilter: {
    type: string | null;
    status: string | null;
    search: string;
  };
}

export type UnifiedAppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CURRENT_VIEW'; payload: string }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_DOMAINS'; payload: Domain[] }
  | { type: 'SET_POLICIES'; payload: Policy[] }
  | { type: 'SET_SYSTEM_STATUS'; payload: Partial<SystemStatus> }
  | { type: 'UPDATE_DOMAIN'; payload: { id: string; updates: Partial<Domain> } }
  | { type: 'UPDATE_POLICY'; payload: { id: string; updates: Partial<Policy> } }
  | { type: 'SET_DOMAIN_FILTER'; payload: Partial<UnifiedAppState['domainFilter']> }
  | { type: 'SET_POLICY_FILTER'; payload: Partial<UnifiedAppState['policyFilter']> }
  | { type: 'REFRESH_DATA' };

const initialState: UnifiedAppState = {
  domains: [],
  policies: [],
  systemStatus: {
    lsegFeed: 'loading',
    teamsStatus: 'loading',
    pendingApprovals: 0,
  },
  isLoading: true,
  currentView: 'dashboard',
  sidebarCollapsed: false,
  domainFilter: {
    status: null,
    source: null,
    search: '',
  },
  policyFilter: {
    type: null,
    status: null,
    search: '',
  },
};

const unifiedAppReducer = (state: UnifiedAppState, action: UnifiedAppAction): UnifiedAppState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_CURRENT_VIEW':
      return { ...state, currentView: action.payload };
    
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    
    case 'SET_DOMAINS':
      return { ...state, domains: action.payload };
    
    case 'SET_POLICIES':
      return { ...state, policies: action.payload };
    
    case 'SET_SYSTEM_STATUS':
      return {
        ...state,
        systemStatus: { ...state.systemStatus, ...action.payload },
      };
    
    case 'UPDATE_DOMAIN':
      return {
        ...state,
        domains: state.domains.map(domain =>
          domain.id === action.payload.id
            ? { ...domain, ...action.payload.updates }
            : domain
        ),
      };
    
    case 'UPDATE_POLICY':
      return {
        ...state,
        policies: state.policies.map(policy =>
          policy.id === action.payload.id
            ? { ...policy, ...action.payload.updates }
            : policy
        ),
      };
    
    case 'SET_DOMAIN_FILTER':
      return {
        ...state,
        domainFilter: { ...state.domainFilter, ...action.payload },
      };
    
    case 'SET_POLICY_FILTER':
      return {
        ...state,
        policyFilter: { ...state.policyFilter, ...action.payload },
      };
    
    case 'REFRESH_DATA':
      return { ...state, isLoading: true };
    
    default:
      return state;
  }
};

// Context
const UnifiedAppContext = createContext<{
  state: UnifiedAppState;
  dispatch: React.Dispatch<UnifiedAppAction>;
  // Actions
  refreshData: (silent?: boolean) => Promise<void>;
  updateDomain: (id: string, updates: Partial<Domain>) => Promise<void>;
  updatePolicy: (id: string, updates: Partial<Policy>) => Promise<void>;
  setCurrentView: (view: string) => void;
} | null>(null);

// Provider
export const UnifiedAppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAuthReady, user } = useMSALAuth();
  const [state, dispatch] = useReducer(unifiedAppReducer, initialState);

  // Actions
  const refreshData = useCallback(async (silent = false) => {
    // Only make API calls if authenticated and ready
    if (!isAuthenticated || !isAuthReady || !user) {
      return;
    }

    try {
      // Only set global loading state if not in silent mode
      if (!silent) {
        dispatch({ type: 'SET_LOADING', payload: true });
      }

      // Fetch domains
      let mappedDomains: Domain[] = [];
      try {
        const domains = await domainService.getDomains();
        // Backend returns domains exactly matching DATA_ACCESS_LAYER_DOCUMENTATION.md - use directly
        mappedDomains = domains.map((d: any) => ({
          ...d,
          // Ensure Date objects for date fields
          approved_at: d.approved_at ? new Date(d.approved_at) : null,
          created_at: new Date(d.created_at),
          updated_at: new Date(d.updated_at),
        }));
        dispatch({ type: 'SET_DOMAINS', payload: mappedDomains });
      } catch (error) {
        console.error('Failed to fetch domains:', error);
        dispatch({ type: 'SET_DOMAINS', payload: [] });
      }

      // Fetch policies
      try {
        const policies = await policyService.getPolicies();
        const mappedPolicies: Policy[] = policies.map(p => ({
          id: p.id || p.name,
          name: p.name,
          type: p.name.endsWith('_OD') ? 'managed' : 'unmanaged',
          userGroups: p.userGroup ? [p.userGroup] : [],
          status: p.driftStatus === 'drift_detected' ? 'drift_detected' : 'active',
          lastSync: p.lastSyncWithTeams || new Date().toISOString(),
          domainCount: p.includedDomains?.length || 0,
          driftCount: p.driftStatus === 'drift_detected' ? 1 : 0,
          allowedDomains: p.includedDomains || [],
          blockedDomains: p.excludedDomains || [],
        }));
        dispatch({ type: 'SET_POLICIES', payload: mappedPolicies });
      } catch (error) {
        console.error('Failed to fetch policies:', error);
        dispatch({ type: 'SET_POLICIES', payload: [] });
      }

      // Check system status - run checks in parallel and update independently
      // This allows fast indicators to update immediately while slow ones are still checking
      
      // Domain Feed status check (non-blocking)
      const checkLsegStatus = async () => {
        try {
          const lsegConfig = await apiClient.get('/lseg/config');
          const configData: any = lsegConfig.data || lsegConfig;
          
          // Test if API is accessible - no fallback logic, must get 200 OK from API
          if (configData.apiUrl && configData.apiKey) {
            try {
              const testResult = await apiClient.post('/lseg/test-connection');
              const testData: any = testResult.data || testResult;
              
              // Update immediately when this check completes
              dispatch({
                type: 'SET_SYSTEM_STATUS',
                payload: {
                  lsegFeed: testData.success ? 'connected' : 'disconnected',
                  lastLsegSync: testData.success ? (testData.lastUpdated || new Date().toISOString()) : undefined,
                },
              });
            } catch (testError) {
              console.error('LSEG connection test failed:', testError);
              dispatch({
                type: 'SET_SYSTEM_STATUS',
                payload: { lsegFeed: 'disconnected' },
              });
            }
          } else {
            dispatch({
              type: 'SET_SYSTEM_STATUS',
              payload: { lsegFeed: 'disconnected' },
            });
          }
        } catch (error) {
          console.error('Failed to check Domain Feed status:', error);
          dispatch({
            type: 'SET_SYSTEM_STATUS',
            payload: { lsegFeed: 'disconnected' },
          });
        }
      };

      // Teams status check (non-blocking)
      const checkTeamsStatus = async () => {
        try {
          const teamsHealth = await teamsService.checkConnection();
          // Update immediately when this check completes
          dispatch({
            type: 'SET_SYSTEM_STATUS',
            payload: {
              teamsStatus: teamsHealth.connected ? 'connected' : 'disconnected',
            },
          });
        } catch (error) {
          console.error('Failed to check Teams status:', error);
          dispatch({
            type: 'SET_SYSTEM_STATUS',
            payload: { teamsStatus: 'disconnected' },
          });
        }
      };

      // Run both checks in parallel - each updates independently as soon as it completes
      Promise.all([
        checkLsegStatus(),
        checkTeamsStatus(),
      ]).catch(error => {
        console.error('Error in system status checks:', error);
      });

    } catch (error) {
      console.error('Failed to refresh data:', error);
    } finally {
      // Only clear global loading state if not in silent mode
      if (!silent) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [isAuthenticated, isAuthReady, user]);

  const updateDomain = useCallback(async (id: string, updates: Partial<Domain>) => {
    try {
      // Update local state immediately for optimistic update
      dispatch({ type: 'UPDATE_DOMAIN', payload: { id, updates } });

      // Update on server
      if (updates.status === 'suspended') {
        // Call suspend API
        // await domainService.suspendDomain(id);
      } else if (updates.status === 'active') {
        // Call reactivate API
        // await domainService.reactivateDomain(id);
      }
    } catch (error) {
      console.error('Failed to update domain:', error);
      // Revert optimistic update on error
      await refreshData();
    }
  }, [dispatch, refreshData]);

  const updatePolicy = useCallback(async (id: string, updates: Partial<Policy>) => {
    try {
      // Update local state immediately for optimistic update
      dispatch({ type: 'UPDATE_POLICY', payload: { id, updates } });

      // Update on server
      // await policyService.updatePolicy(id, updates);
    } catch (error) {
      console.error('Failed to update policy:', error);
      // Revert optimistic update on error
      await refreshData();
    }
  }, [dispatch, refreshData]);

  const setCurrentView = useCallback((view: string) => {
    dispatch({ type: 'SET_CURRENT_VIEW', payload: view });
  }, [dispatch]);

  // Load data on mount - only when authenticated
  useEffect(() => {
    if (isAuthenticated && isAuthReady && user) {
      refreshData();
    }
  }, [isAuthenticated, isAuthReady, user, refreshData]);

  const contextValue = useMemo(() => ({
    state,
    dispatch,
    refreshData,
    updateDomain,
    updatePolicy,
    setCurrentView,
  }), [state, dispatch, refreshData, updateDomain, updatePolicy, setCurrentView]);

  return (
    <UnifiedAppContext.Provider value={contextValue}>
      <span hidden data-testid="unified-app-provider" />
      {children}
    </UnifiedAppContext.Provider>
  );
};

// Hook
export const useUnifiedApp = () => {
  const context = useContext(UnifiedAppContext);
  if (!context) {
    throw new Error('useUnifiedApp must be used within a UnifiedAppProvider');
  }
  return context;
};

// Selectors for derived state
export const useFilteredDomains = () => {
  const { state } = useUnifiedApp();
  const { domains, domainFilter } = state;

  return domains.filter(domain => {
    if (domainFilter.status && domain.status !== domainFilter.status) {
      return false;
    }
    if (domainFilter.source && domain.source !== domainFilter.source) {
      return false;
    }
    if (domainFilter.search && !domain.domain.toLowerCase().includes(domainFilter.search.toLowerCase())) {
      return false;
    }
    return true;
  });
};

export const useFilteredPolicies = () => {
  const { state } = useUnifiedApp();
  const { policies, policyFilter } = state;

  return policies.filter(policy => {
    if (policyFilter.type && policy.type !== policyFilter.type) {
      return false;
    }
    if (policyFilter.status && policy.status !== policyFilter.status) {
      return false;
    }
    if (policyFilter.search && !policy.name.toLowerCase().includes(policyFilter.search.toLowerCase())) {
      return false;
    }
    return true;
  });
};

export const useDashboardStats = () => {
  const { state } = useUnifiedApp();
  const { domains, policies, systemStatus } = state;

  // Calculate actual drift count from policies
  const driftCount = policies.filter(p => p.status === 'drift_detected').length;

  // Match the exact logic used in Domains.tsx for consistency
  const pendingDomains = domains.filter(d => d.approval_status === 'pending').length;
  const approvedDomains = domains.filter(d => d.approval_status === 'approved').length;
  const suspendedDomains = domains.filter(d => d.approval_status === 'suspended').length;

  return {
    totalDomains: domains.length,
    approvedDomains: approvedDomains,
    suspendedDomains: suspendedDomains,
    pendingDomains: pendingDomains,
    newDomains: domains.filter(d => {
      const addedDate = new Date(d.created_at);
      const daysSinceAdded = (Date.now() - addedDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceAdded <= 7;
    }).length,
    
    totalPolicies: policies.length,
    managedPolicies: policies.filter(p => p.type === 'managed').length,
    unmanagedPolicies: policies.filter(p => p.type === 'unmanaged').length,
    policiesWithDrift: driftCount,
    
    systemStatus,
    
    domainUsage: {
      used: domains.length,
      total: 4000,
      percentage: Math.round((domains.length / 4000) * 100),
    },
  };
};